import React, { ReactNode } from 'react'

import { Placement } from '@popperjs/core'

import { Box, Dropdown, Flex, Icon, Text } from 'stardust'

import styled from 'styled-components'

import colors from '~/ui-components/tokens/colors'
import { createTypography } from '~/utils'

export interface DropdownMenuItemProps {
  label: string
  icon?: JSX.Element
  onClick(): unknown
  disabled?: boolean
  textAlign?: string
}

interface Props {
  disabled?: boolean
  items: DropdownMenuItemProps[]
  label: ReactNode
  textAlign?: string
  placement?: Placement
  portal?: boolean
  showArrow?: boolean
  zIndex?: number
  callFrom: string
}

const HoverFlex = styled(Flex)<{ disabled?: boolean; textAlign?: string }>`
  min-width: 200px;
  white-space: nowrap;
  color: ${(props) => props.theme.colors.textPrimaryMedium};
  transition: background 0.25s ease;
  user-select: none;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  justify-content: ${(props) => props.textAlign || 'flex-start'};

  &:hover {
    ${(props) => !props.disabled && `background: ${colors.cosmicShade4};`}
  }

  text-decoration: none;

  &:last-child {
    border-bottom: 0;
  }
`

export const DropdownMenuItem = ({ label, icon, onClick, disabled, textAlign }: DropdownMenuItemProps) => {
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.stopPropagation()
    onClick()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onClick()
    }
  }

  return (
    <HoverFlex
      borderBottom="1px solid"
      borderColor="surfacePrimaryBorder"
      py="14px"
      px="20px"
      onClick={disabled ? undefined : handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      textAlign={textAlign}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      data-qa={`dropdown-menu-item-${label.replace(' ', '-').toLocaleLowerCase()}`}>
      {icon && (
        <Flex height="32px" width="24px" mr={3} alignItems="center" justifyContent="center">
          {icon}
        </Flex>
      )}
      <Box py={1}>
        <Text {...createTypography('medium')} color={disabled ? 'textPrimaryLow' : 'textPrimary'}>
          {label}
        </Text>
      </Box>
    </HoverFlex>
  )
}

const DropdownMenu = ({
  disabled,
  items,
  label,
  textAlign,
  placement,
  portal,
  showArrow = true,
  zIndex,
  callFrom,
}: Props) => {
  const color = disabled ? 'textPrimaryLow' : 'textPrimaryMedium'
  const safeItems = disabled ? [] : items

  return (
    <Box cursor="pointer" width={1} data-qa={`dropdown-menu-${callFrom.replace(' ', '-')}`}>
      <Dropdown
        portal={portal}
        renderButton={({ handleClose }) => (
          <Flex alignItems="center" width={1} onClick={handleClose}>
            <Flex flexGrow={1}>
              {typeof label === 'string' ? <Text.span color={color}>{label}</Text.span> : label}
            </Flex>
            {showArrow && <Icon color={color} ml={1} name="chevronDown" />}
          </Flex>
        )}
        render={({ handleClose }) => (
          <Flex
            flexDirection="column"
            maxHeight={'75vh'}
            overflow={'auto'}
            data-item={`dropdown-menu-list-${callFrom.replace(' ', '-')}`}
            data-qa={`dropdown-menu-list-${callFrom.replace(' ', '-')}`}>
            {safeItems.map((item) => {
              const onClick = () => {
                handleClose()
                item.onClick()
              }

              return (
                <DropdownMenuItem
                  key={item.label}
                  label={item.label}
                  icon={item.icon}
                  onClick={onClick}
                  disabled={item.disabled}
                  textAlign={item.textAlign ?? textAlign}
                />
              )
            })}
          </Flex>
        )}
        zIndex={zIndex}
        placement={placement}
      />
    </Box>
  )
}

DropdownMenu.displayName = 'DropdownMenu'
DropdownMenuItem.displayName = 'DropdownMenuItem'

export default React.memo(DropdownMenu)
